import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  Inject,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';

import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';

import { TablerIconsModule } from 'angular-tabler-icons';
import { EquipmentModalComponent } from 'src/app/components/modals/equipment-modal/equipment-modal.component';
import { EnvironmentModalComponent } from 'src/app/components/modals/environment-modal/environment-modal.component';
import { MaterialModule } from 'src/app/material.module';
import { EquipmentService } from 'src/app/services/apps/equipment/equipment.service';
import { CompanyService } from 'src/app/services/apps/company/company.service';
import {
  DemandService,
  ManagementCompanyOption,
} from 'src/app/services/apps/demand/demand.service';
import { EnvironmentService } from 'src/app/services/apps/environment/environment.service';
import { DemandModalData } from 'src/assets/types/interface/demand-modal-data.interface';
import { ServiceNature } from 'src/assets/types/enums/service-nature.enum';
import {
  CreateDemand,
  DemandSpecialty,
  DemandServiceType,
} from 'src/assets/types/interface/demand.interface';
import { Environment } from 'src/assets/types/interface/environment.interface';
import { Equipment } from 'src/assets/types/interface/equipament.interface';
import { Company } from 'src/assets/types/interface/company.interface';
import { catchError, finalize, of } from 'rxjs';
import { ImageUploaderComponent } from 'src/app/components/image-uploader/image-uploader.component';
import {
  SearchableMultiSelectComponent,
  SearchableMultiSelectOption,
} from 'src/app/components/searchable-multi-select/searchable-multi-select.component';
import {
  SearchableSingleSelectComponent,
  SearchableSingleSelectOption,
} from 'src/app/components/searchable-single-select/searchable-single-select.component';

export interface specialties {
  name: string;
  uuid?: string;
}

type ApiFieldStatus = 'idle' | 'empty' | 'warning' | 'error';

interface ApiFieldState {
  status: ApiFieldStatus;
  message: string;
}

interface NormalizedListResult<T> {
  items: T[];
  partial: boolean;
}

@Component({
  selector: 'app-add-demand-dialog',
  standalone: true,
  imports: [
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    ImageUploaderComponent,
    TablerIconsModule,
    SearchableMultiSelectComponent,
    SearchableSingleSelectComponent,
  ],
  templateUrl: 'add-demand.component.html',
  styleUrl: 'add-demand.component.scss',
})
export class AddDemandModalComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  requestCatalogLoaded = false;
  demandForm: FormGroup;
  managementCompanies: ManagementCompanyOption[] = [];

  data: DemandModalData = {
    companies: [],
    environments: [],
    specialties: [],
    equipments: [],
    occurrence: '',
    selectedSpecialties: [],
    types: [],
    needsTechnicalVisit: false,
  };

  environment: Environment = {
    uuid: '',
    name: '',
    details: '',
    company: {},
  };

  equipment: Partial<Equipment> = {
    name: '',
    serial_number: '',
    uuid: '',
  };

  withAi = false;

  showCancel = true;
  showConfirm = true;
  showAlert = false;
  alertMessage = '';
  formErrorMessage = '';
  aiWarningMessage = '';

  loadingSpecialties = false;
  loadingCompanies = false;
  loadingTypes = false;
  loadingEnvironments = false;
  loadingEquipments = false;
  isSubmittingRequest = false;

  specialtiesApiState: ApiFieldState = this.createApiFieldState();
  companiesApiState: ApiFieldState = this.createApiFieldState();
  typesApiState: ApiFieldState = this.createApiFieldState();
  environmentsApiState: ApiFieldState = this.createApiFieldState();
  equipmentsApiState: ApiFieldState = this.createApiFieldState();

  imageFiles: any;
  @ViewChild(ImageUploaderComponent)
  imageUploader!: ImageUploaderComponent;

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<AddDemandModalComponent>,
    private companyService: CompanyService,
    private demandService: DemandService,
    private equipmentService: EquipmentService,
    private environmentService: EnvironmentService,
    private router: Router,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA)
    public injectedData: {
      data: DemandModalData | null;
      demand: CreateDemand | null;
      occurrence: string | null;
      withAi: boolean | null;
      aiWarningMessage?: string | null;
    },
  ) {
    this.data = { ...this.data, ...(this.injectedData.data ?? {}) };
    this.withAi = this.injectedData.withAi ?? false;
    this.aiWarningMessage = this.injectedData.aiWarningMessage ?? '';

    const demand = this.getDemandByInjectedOrAlreadyLoadedData();
    this.inicializeFormGroup(demand);
    this.data.occurrence = demand.description;
    // AI/injected values are suggestions. Only the request catalog grants eligibility.
    this.data.specialties = [];
    this.data.types = [];
  }

  ngOnInit(): void {
    const hasData = !!this.data;
    const company = this.data.companies?.[0];
    const environment = this.data.environments?.[0];

    if (hasData && !this.injectedData.demand) {
      this.demandForm.patchValue({
        localDetail: company?.uuid ?? '',
        environmentUuid: environment?.uuid ?? null,
      });
    }

    const isValidCompany =
      company && this.data.companies.some((c) => c.uuid === company.uuid);

    if (this.withAi && isValidCompany) {
      this.demandForm.patchValue({
        localDetail: company?.uuid ?? '',
        environmentUuid: environment?.uuid ?? null,
      });
    }

    this.loadRequestOptions();
    this.loadCompanies();
    this.loadManagementCompanies();
    this.configureFlowSelection();
  }

  loadRequestOptions() {
    this.requestCatalogLoaded = false;
    this.loadingSpecialties = true;
    this.loadingTypes = true;
    this.specialtiesApiState = this.createApiFieldState();
    this.typesApiState = this.createApiFieldState();
    this.demandService
      .getRequestOptions()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingSpecialties = false;
          this.loadingTypes = false;
        }),
      )
      .subscribe({
        next: (response) => {
          const specialties = this.normalizeListResponse<DemandSpecialty>(
            response?.specialties,
            this.isValidDemandSpecialty,
          );
          const types = this.normalizeListResponse<DemandServiceType>(
            response?.types,
            this.isValidDemandServiceType,
          );
          this.data.specialties = specialties.items.filter((item) =>
            this.isRequestableNature(item.nature),
          );
          this.data.types = types.items.filter((item) =>
            this.isRequestableNature(item.nature),
          );

          const previousSpecialties = this.selectedSpecialtyUuids;
          const selected = this.data.specialties.filter((item) =>
            previousSpecialties.includes(item.uuid),
          );
          const previousType = this.demandForm.get('serviceTypeUuid')?.value;
          const allowedType = this.data.types.find(
            (item) => item.uuid === previousType,
          );
          this.demandForm.patchValue({
            specialtyUuids: selected,
            serviceTypeUuid: allowedType?.uuid ?? null,
          });
          this.data.selectedSpecialties = selected;
          if (
            selected.length !== previousSpecialties.length ||
            (previousType && !allowedType)
          ) {
            this.aiWarningMessage =
              'Algumas opções não estão disponíveis para solicitação pela Profinders. Revise o tipo e as especialidades.';
          }
          this.requestCatalogLoaded = true;
          this.specialtiesApiState = this.catalogFieldState(
            this.data.specialties.length,
            specialties.partial,
            'especialidades',
          );
          this.typesApiState = this.catalogFieldState(
            this.data.types.length,
            types.partial,
            'tipos',
          );
        },
        error: () => {
          this.data.specialties = [];
          this.data.types = [];
          this.data.selectedSpecialties = [];
          this.demandForm.patchValue({
            specialtyUuids: [],
            serviceTypeUuid: null,
          });
          this.specialtiesApiState = {
            status: 'error',
            message: 'Não foi possível carregar as opções de solicitação.',
          };
          this.typesApiState = { ...this.specialtiesApiState };
          this.formErrorMessage = this.specialtiesApiState.message;
        },
      });
  }

  private isRequestableNature(nature: ServiceNature | undefined): boolean {
    return (
      nature === ServiceNature.GENERAL || nature === ServiceNature.CORRECTIVE
    );
  }

  private catalogFieldState(
    count: number,
    partial: boolean,
    label: string,
  ): ApiFieldState {
    if (!count)
      return { status: 'empty', message: `Não há ${label} disponíveis.` };
    if (partial)
      return {
        status: 'warning',
        message: 'Algumas opções incompletas foram ignoradas.',
      };
    return this.createApiFieldState();
  }

  loadCompanies() {
    this.loadingCompanies = true;
    this.companiesApiState = this.createApiFieldState();
    this.companyService
      .getCompanies()
      .pipe(
        finalize(() => {
          this.loadingCompanies = false;
        }),
      )
      .subscribe({
        next: (companies) => {
          const { items, partial } = this.normalizeListResponse<Company>(
            companies,
            this.isValidCompany,
          );

          this.data.companies = items;

          if (partial) {
            this.companiesApiState = {
              status: 'warning',
              message:
                'Algumas unidades retornaram incompletas e foram ignoradas.',
            };
          } else if (!items.length) {
            this.companiesApiState = {
              status: 'empty',
              message: 'Nenhuma unidade disponivel.',
            };
          }
        },
        error: () => {
          this.data.companies = [];
          this.companiesApiState = {
            status: 'error',
            message: 'Nao foi possivel carregar as unidades.',
          };
          this.formErrorMessage = 'Nao foi possivel carregar as unidades.';
        },
      });
  }

  loadManagementCompanies() {
    this.demandService.getManagementCompanies().subscribe({
      next: (response) => {
        this.managementCompanies = Array.isArray(response) ? response : [];
      },
      error: () => {
        this.managementCompanies = [];
      },
    });
  }

  configureFlowSelection() {
    this.demandForm.get('flowSelection')?.valueChanges.subscribe((value) => {
      const managementCompanyUuid = this.resolveManagementCompanyUuid(value);

      this.demandForm.patchValue(
        {
          source: managementCompanyUuid ? 'GERENCIADA' : value,
          managementCompanyUuid,
        },
        { emitEvent: false },
      );
    });
  }

  resolveManagementCompanyUuid(value: string | null | undefined) {
    return value?.startsWith('MANAGEMENT_COMPANY:')
      ? value.replace('MANAGEMENT_COMPANY:', '')
      : null;
  }

  loadEnvironments() {
    const companyUuid = this.demandForm.value.localDetail ?? '';
    if (!companyUuid) {
      this.data.environments = [];
      this.data.equipments = [];
      this.environmentsApiState = this.createApiFieldState();
      this.equipmentsApiState = this.createApiFieldState();
      return;
    }

    this.loadingEnvironments = true;
    this.environmentsApiState = this.createApiFieldState();
    this.equipmentsApiState = this.createApiFieldState();

    try {
      this.environmentService
        .getEnvironments(companyUuid)
        .pipe(
          catchError((error) => {
            if (error.status === 404) {
              this.environmentsApiState = {
                status: 'empty',
                message: 'Nenhum ambiente disponivel na unidade selecionada.',
              };
              console.warn('Ambientes não encontrados para a empresa.');
              return of([]);
            }

            console.error('Erro ao buscar ambientes:', error);
            this.environmentsApiState = {
              status: 'error',
              message: 'Nao foi possivel carregar os ambientes desta unidade.',
            };
            return of([]);
          }),
          finalize(() => {
            this.loadingEnvironments = false;
          }),
        )
        .subscribe((res) => {
          const { items, partial } = this.normalizeListResponse<Environment>(
            res,
            this.isValidEnvironment,
          );

          this.data.environments = items;

          if (partial) {
            this.environmentsApiState = {
              status: 'warning',
              message:
                'Alguns ambientes retornaram incompletos e foram ignorados.',
            };
          } else if (
            !items.length &&
            this.environmentsApiState.status === 'idle'
          ) {
            this.environmentsApiState = {
              status: 'empty',
              message: 'Nenhum ambiente disponivel na unidade selecionada.',
            };
          }
        });
    } catch (error: any) {
      if (error.status === 404) {
        console.warn('Nenhum ambiente encontrado:', error.error?.message);
        this.data.environments = [];
        this.environmentsApiState = {
          status: 'empty',
          message: 'Nenhum ambiente disponivel na unidade selecionada.',
        };
      } else {
        console.error('Erro ao buscar ambientes:', error);
        this.environmentsApiState = {
          status: 'error',
          message: 'Nao foi possivel carregar os ambientes desta unidade.',
        };
      }
      this.loadingEnvironments = false;
    }
    this.data.equipments = [];
  }

  loadEquipments(environmentUuid: string) {
    this.loadingEquipments = true;
    this.equipmentsApiState = this.createApiFieldState();
    this.equipmentService
      .getEquipments(environmentUuid)
      .pipe(
        catchError((error) => {
          if (error.status === 404) {
            this.equipmentsApiState = {
              status: 'empty',
              message:
                'Nenhum equipamento disponivel para o ambiente selecionado.',
            };
            return of([]);
          }

          this.equipmentsApiState = {
            status: 'error',
            message:
              'Nao foi possivel carregar os equipamentos deste ambiente.',
          };
          return of([]);
        }),
        finalize(() => {
          this.loadingEquipments = false;
        }),
      )
      .subscribe((res) => {
        const { items, partial } = this.normalizeListResponse<Equipment>(
          res,
          this.isValidEquipment,
        );

        this.data.equipments = items;

        if (partial) {
          this.equipmentsApiState = {
            status: 'warning',
            message:
              'Alguns equipamentos retornaram incompletos e foram ignorados.',
          };
        } else if (!items.length && this.equipmentsApiState.status === 'idle') {
          this.equipmentsApiState = {
            status: 'empty',
            message:
              'Nenhum equipamento disponivel para o ambiente selecionado.',
          };
        }
      });
  }

  async createDemand() {
    this.formErrorMessage = '';

    if (
      !this.requestCatalogLoaded ||
      this.loadingTypes ||
      this.loadingSpecialties
    ) {
      this.formErrorMessage =
        'Aguarde o carregamento das opções de solicitação.';
      return;
    }

    if (this.demandForm.invalid) {
      this.demandForm.markAllAsTouched();
      this.formErrorMessage =
        'Preencha os campos obrigatorios antes de solicitar o servico.';
      return;
    }

    if (this.isSubmittingRequest) return;

    const formValue = this.demandForm.value;

    const demand = {
      ...formValue,
      flowSelection: undefined,
      specialtyUuids: formValue.specialtyUuids
        .map((s: DemandSpecialty | string) =>
          typeof s === 'string' ? s : s.uuid,
        )
        .filter(Boolean),
    };

    this.imageFiles = this.imageUploader.getPayloadUpload();
    this.data.payloadImages = this.imageFiles;

    try {
      this.isSubmittingRequest = true;
      this.demandService
        .createDemand(demand.localDetail, demand, this.data.payloadImages)
        .pipe(
          finalize(() => {
            this.isSubmittingRequest = false;
          }),
        )
        .subscribe({
          next: () => {
            this.router.navigate(['/gestao/solicitacoes']);
            this.closeDialog();
          },
          error: (error) => {
            console.error('Erro ao criar solicitacao:', error);
            this.formErrorMessage =
              error.error?.code === 'PREVENTIVE_REQUEST_NOT_ALLOWED' ||
              error.error?.body?.code === 'PREVENTIVE_REQUEST_NOT_ALLOWED'
                ? 'Serviços preventivos não podem ser solicitados pela Profinders.'
                : 'Nao foi possivel criar a solicitacao. Revise os dados e tente novamente.';
          },
        });
    } catch (error) {
      this.isSubmittingRequest = false;
      this.formErrorMessage = 'Nao foi possivel criar a solicitacao.';
      console.error('Error creating demand:', error);
    }
  }

  onCompanyChange() {
    this.data.environments = [];
    this.data.equipments = [];
    this.environmentsApiState = this.createApiFieldState();
    this.equipmentsApiState = this.createApiFieldState();
    this.demandForm.patchValue({
      environmentUuid: null,
      equipmentUuid: null,
    });
    this.loadEnvironments();
  }

  onEnvironmentChange(environmentUuid: string | null | undefined) {
    this.data.equipments = [];
    this.equipmentsApiState = this.createApiFieldState();
    this.demandForm.get('equipmentUuid')?.setValue(null);

    if (!environmentUuid) {
      return;
    }

    this.loadEquipments(environmentUuid);
  }

  hasControlError(controlName: string, errorName: string): boolean {
    const control = this.demandForm.get(controlName);
    return Boolean(
      control?.hasError(errorName) && (control.touched || control.dirty),
    );
  }

  get isSubmitDisabled(): boolean {
    return (
      !this.requestCatalogLoaded ||
      this.loadingTypes ||
      this.loadingSpecialties ||
      this.isSubmittingRequest ||
      this.demandForm.invalid
    );
  }

  get environmentEmptyMessage(): string {
    if (!this.demandForm.get('localDetail')?.value) {
      return 'Selecione uma matriz ou filial para listar ambientes.';
    }

    if (this.environmentsApiState.status !== 'idle') {
      return this.environmentsApiState.message;
    }

    return 'Nenhum ambiente disponivel na unidade selecionada.';
  }

  get equipmentEmptyMessage(): string {
    if (!this.demandForm.get('localDetail')?.value) {
      return 'Selecione uma matriz ou filial para listar equipamentos.';
    }

    if (!this.demandForm.get('environmentUuid')?.value) {
      return 'Selecione um ambiente para listar equipamentos.';
    }

    if (this.equipmentsApiState.status !== 'idle') {
      return this.equipmentsApiState.message;
    }

    return 'Nenhum equipamento disponivel para o ambiente selecionado.';
  }

  get selectedSpecialtiesCount(): number {
    const specialties = this.demandForm.get('specialtyUuids')?.value ?? [];
    return Array.isArray(specialties) ? specialties.length : 0;
  }

  get specialtyOptions(): SearchableMultiSelectOption[] {
    return this.data.specialties
      .filter((specialty) => Boolean(specialty.uuid))
      .map((specialty) => ({
        label: specialty.name,
        value: specialty.uuid as string,
      }));
  }

  get serviceTypeOptions(): SearchableSingleSelectOption[] {
    return this.data.types
      .filter((type) => Boolean(type.uuid))
      .map((type) => ({
        label: type.name,
        value: type.uuid as string,
      }));
  }

  get companyOptions(): SearchableSingleSelectOption[] {
    return this.data.companies
      .filter((company) => Boolean(company.uuid))
      .map((company) => ({
        label: company.name ?? 'Unidade sem nome',
        value: company.uuid as string,
      }));
  }

  get environmentOptions(): SearchableSingleSelectOption[] {
    return this.data.environments
      .filter((environment) => Boolean(environment.uuid))
      .map((environment) => ({
        label: environment.name,
        value: environment.uuid,
      }));
  }

  get equipmentOptions(): SearchableSingleSelectOption[] {
    return this.data.equipments
      .filter((equipment) => Boolean(equipment.uuid))
      .map((equipment) => ({
        label: equipment.name ?? 'Equipamento sem nome',
        value: equipment.uuid as string,
      }));
  }

  get managementModelOptions(): SearchableSingleSelectOption[] {
    return [
      {
        label: 'Auto-Gestão',
        value: 'AUTO_GESTAO',
      },
      {
        label: 'ProFinders',
        value: 'MODERACAO',
      },
      ...this.managementCompanies.map((managementCompany) => ({
        label: managementCompany.name,
        value: `MANAGEMENT_COMPANY:${managementCompany.uuid}`,
      })),
    ];
  }

  get selectedSpecialtyUuids(): string[] {
    const specialties = this.demandForm.get('specialtyUuids')?.value ?? [];
    if (!Array.isArray(specialties)) return [];

    return specialties
      .map((specialty: DemandSpecialty | string) =>
        typeof specialty === 'string' ? specialty : specialty.uuid,
      )
      .filter((uuid: string | undefined): uuid is string => Boolean(uuid));
  }

  get occurrenceLength(): number {
    return this.demandForm.get('description')?.value?.trim?.().length ?? 0;
  }

  openEnvironmentModal(): void {
    const payload = {
      environment: {
        uuid: '',
        name: '',
        description: '',
        company: {
          uuid: this.demandForm.value.localDetail,
          name: '',
        },
      },
      business_units: this.data.companies,
    };

    const dialogRef = this.dialog.open(EnvironmentModalComponent, {
      width: '1200px',
      data: payload,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((environment) => {
      if (environment) {
        this.data.environments.push(environment);
      }
    });
  }

  openEquipmentModal(): void {
    const payload = {
      uuid: '',
      name: '',
      serial_number: '',
      description: '',
      environment: {
        uuid: this.demandForm.value.environmentUuid,
        name: '',
        company: {
          uuid: this.demandForm.value.localDetail,
          name: '',
        },
        details: '',
      },
    };

    const dialogRef = this.dialog.open(EquipmentModalComponent, {
      width: '1200px',
      data: payload,
      autoFocus: true,
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe((equipment) => {
      if (equipment) {
        this.data.equipments.push(equipment);
      }
    });
  }

  selectSpecialty(values: DemandSpecialty[]) {
    this.data.selectedSpecialties = values;
  }

  handleSpecialtyUuidsChange(specialtyUuids: string[]): void {
    const selectedSpecialties = this.data.specialties.filter((specialty) =>
      specialtyUuids.includes(specialty.uuid),
    );
    const specialtyControl = this.demandForm.get('specialtyUuids');

    specialtyControl?.setValue(selectedSpecialties);
    specialtyControl?.markAsDirty();
    specialtyControl?.markAsTouched();
    specialtyControl?.updateValueAndValidity();
    this.selectSpecialty(selectedSpecialties);
  }

  handleServiceTypeUuidChange(serviceTypeUuid: string | null): void {
    this.updateControlValue('serviceTypeUuid', serviceTypeUuid);
  }

  handleCompanyUuidChange(companyUuid: string | null): void {
    this.updateControlValue('localDetail', companyUuid);
    this.onCompanyChange();
  }

  handleEnvironmentUuidChange(environmentUuid: string | null): void {
    this.updateControlValue('environmentUuid', environmentUuid);
    this.onEnvironmentChange(environmentUuid);
  }

  handleEquipmentUuidChange(equipmentUuid: string | null): void {
    this.updateControlValue('equipmentUuid', equipmentUuid);
  }

  handleFlowSelectionChange(flowSelection: string | null): void {
    this.updateControlValue('flowSelection', flowSelection);
  }

  private updateControlValue(controlName: string, value: string | null): void {
    const control = this.demandForm.get(controlName);

    control?.setValue(value);
    control?.markAsDirty();
    control?.markAsTouched();
    control?.updateValueAndValidity();
  }

  unselectSpecialty(specialtie: DemandSpecialty): void {
    this.data.selectedSpecialties = this.data.selectedSpecialties.filter(
      (item: DemandSpecialty) => item.uuid !== specialtie.uuid,
    );
  }

  closeDialog(): void {
    this.dialogRef.close({ event: 'Cancel' });
  }

  isFormValid(): boolean {
    return this.demandForm.valid;
  }

  arrayNotEmptyValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    return Array.isArray(value) && value.length === 0
      ? { arrayEmpty: true }
      : null;
  }

  trackByUuid(index: number, item: any): string {
    return item.uuid;
  }

  getDemandByInjectedOrAlreadyLoadedData() {
    return {
      serviceTypeUuid: this.injectedData.demand?.serviceTypeUuid ?? null,
      specialtyUuids: (() => {
        const selected =
          this.data.specialties.filter((s) =>
            (this.injectedData.demand?.specialtyUuids ?? []).includes(s.uuid),
          ) ?? [];
        return selected.length > 0
          ? selected
          : (this.injectedData.data?.specialties?.slice(0, 3) ?? []);
      })(),
      localDetail:
        this.injectedData.demand?.localDetail ??
        this.injectedData.data?.companies?.[0]?.uuid ??
        null,
      environmentUuid:
        this.injectedData.demand?.environmentUuid ??
        this.injectedData.data?.environments?.[0]?.uuid ??
        null,
      equipmentUuid:
        this.injectedData.demand?.equipmentUuid ??
        (this.injectedData.data !== null &&
        this.injectedData.data?.equipments?.length > 0
          ? this.injectedData.data?.equipments[0].uuid
          : null),
      needsTechnicalVisit:
        this.injectedData.demand?.needsTechnicalVisit ?? false,
      source: this.injectedData.demand?.source ?? 'AUTO_GESTAO',
      managementCompanyUuid:
        this.injectedData.demand?.managementCompanyUuid ?? null,
      description:
        this.injectedData.occurrence ??
        this.injectedData.demand?.description ??
        '',
    };
  }

  inicializeFormGroup(demand: any) {
    this.demandForm = this.fb.group({
      serviceTypeUuid: [demand.serviceTypeUuid, Validators.required],
      specialtyUuids: [
        demand.specialtyUuids,
        [Validators.required, Validators.minLength(1)],
      ],
      localDetail: [demand.localDetail, Validators.required],
      environmentUuid: [demand.environmentUuid],
      equipmentUuid: [demand.equipmentUuid],
      needsTechnicalVisit: [demand.needsTechnicalVisit],
      flowSelection: [
        demand.managementCompanyUuid
          ? `MANAGEMENT_COMPANY:${demand.managementCompanyUuid}`
          : demand.source,
        Validators.required,
      ],
      source: [demand.source, Validators.required],
      managementCompanyUuid: [demand.managementCompanyUuid],
      description: [
        demand.description,
        [Validators.required, Validators.minLength(10)],
      ],
    });
  }

  resetEquipmentSelection(event: MouseEvent) {
    event.stopPropagation();
    this.demandForm.get('equipmentUuid')?.setValue(null);
  }

  resetEnvironmentSelection(event: MouseEvent) {
    event.stopPropagation();
    this.data.equipments = [];
    this.demandForm.get('environmentUuid')?.setValue(null);
    this.demandForm.get('equipmentUuid')?.setValue(null);
  }

  private createApiFieldState(): ApiFieldState {
    return {
      status: 'idle',
      message: '',
    };
  }

  private normalizeListResponse<T>(
    response: unknown,
    isValidItem: (item: unknown) => item is T,
  ): NormalizedListResult<T> {
    const responseList = this.getArrayFromResponse(response);

    if (!responseList) {
      return {
        items: [],
        partial: true,
      };
    }

    const items = responseList.filter(isValidItem);

    return {
      items,
      partial: items.length !== responseList.length,
    };
  }

  private getArrayFromResponse(response: unknown): unknown[] | null {
    if (Array.isArray(response)) {
      return response;
    }

    if (!this.isRecord(response)) {
      return null;
    }

    const possibleArrayKeys = ['data', 'body', 'items', 'results'];

    for (const key of possibleArrayKeys) {
      const value = response[key];

      if (Array.isArray(value)) {
        return value;
      }
    }

    return null;
  }

  private isValidDemandSpecialty = (item: unknown): item is DemandSpecialty => {
    if (!this.isRecord(item)) return false;

    return this.hasText(item['uuid']) && this.hasText(item['name']);
  };

  private isValidDemandServiceType = (
    item: unknown,
  ): item is DemandServiceType => {
    if (!this.isRecord(item)) return false;

    return this.hasText(item['uuid']) && this.hasText(item['name']);
  };

  private isValidCompany = (item: unknown): item is Company => {
    if (!this.isRecord(item)) return false;

    return this.hasText(item['uuid']);
  };

  private isValidEnvironment = (item: unknown): item is Environment => {
    if (!this.isRecord(item)) return false;

    return this.hasText(item['uuid']) && this.hasText(item['name']);
  };

  private isValidEquipment = (item: unknown): item is Equipment => {
    if (!this.isRecord(item)) return false;

    return this.hasText(item['uuid']) && this.hasText(item['name']);
  };

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
  }

  private hasText(value: unknown): value is string {
    return typeof value === 'string' && value.trim().length > 0;
  }
}
